import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, LinearProgress, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import Popup from "../../../components/Popup";
import AxionControls from "../../../components/controls/AxionControls";
import { getAssetHierarchyPalette, getContainedButtonSx, getDialogSectionBoxSx, getOutlinedButtonSx } from "../assetHierarchyStyles";
import { AssetDocumentType, getAssetDocumentTypeOptions } from "../assetDocumentEnums";
import { uploadAssetDocument } from "../../../fetchers/cmmsAssetDocument";
import cmmsMessages from "../cmmsMessages";
import messages from "../messages";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { formatCmmsError } from "./CmmsStates";
import { checkUploadAgainstLimits, useCmmsUploadLimits } from "../../../hooks/useCmmsUploadLimits";
import { formatFileSize } from "../cmmsFormat";

const stripExtension = (fileName) => (fileName ?? "").replace(/\.[^./\\]+$/, "");

const createInitialState = (documentType) => ({
  documentType,
  title: "",
  description: "",
});

/*
 * The last three props are for a caller that uploads for a purpose of its own, as the machine
 * card does for its overview image. acceptedContentTypes narrows what the API accepts and can
 * never widen it: a file has to pass the server's limits first. onUploaded gets the new
 * document once the dialog has closed.
 */
export default function AssetDocumentUploadDialog({
  assetNodeId,
  companyKey,
  onClose,
  open,
  initialDocumentType = AssetDocumentType.Manual,
  acceptedContentTypes = null,
  onUploaded,
}) {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const queryClient = useQueryClient();

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [formState, setFormState] = useState(() => createInitialState(initialDocumentType));
  const [file, setFile] = useState(null);
  const [isTitleEdited, setIsTitleEdited] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);

  const typeOptions = useMemo(() => getAssetDocumentTypeOptions(intl), [intl]);
  const uploadLimits = useCmmsUploadLimits();

  useEffect(() => {
    if (open) {
      setFormState(createInitialState(initialDocumentType));
      setFile(null);
      setIsTitleEdited(false);
      setDragOver(false);
      setUploadProgress(null);
    }
  }, [open, initialDocumentType]);

  const uploadMutation = useMutation({
    mutationFn: () => uploadAssetDocument(assetNodeId, {
      companyKey,
      documentType: formState.documentType,
      title: formState.title,
      description: formState.description,
      file,
      onProgress: (event) => {
        if (event.total) {
          setUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    }),
  });

  const acceptFile = (candidate) => {
    if (!candidate) return;

    // Limits come from the API rather than a constant, so the client and the server
    // cannot disagree about what will be accepted.
    // The caller's narrower list is checked as well, not instead: the drop zone ignores the
    // inputs' accept attribute, so this is the only thing that holds it for a dragged file.
    const rejection = checkUploadAgainstLimits(candidate, uploadLimits)
      ?? checkUploadAgainstLimits(candidate, { allowedContentTypes: acceptedContentTypes });

    if (rejection === "size") {
      pushErrorMessage(intl.formatMessage(messages.uploadTooLarge, {
        name: candidate.name,
        size: formatFileSize(candidate.size),
        max: formatFileSize(uploadLimits.maxBytes),
      }));
      return;
    }

    if (rejection === "type") {
      pushErrorMessage(intl.formatMessage(messages.uploadTypeNotAllowed, {
        name: candidate.name,
        type: candidate.type || intl.formatMessage(messages.uploadTypeUnknown),
      }));
      return;
    }

    setFile(candidate);
    // Prefill the title from the filename until the user types their own, the
    // same way AssetNodeFormDialog prefills a node name from the machine.
    if (!isTitleEdited) {
      setFormState((current) => ({ ...current, title: stripExtension(candidate.name) }));
    }
  };

  const handleFileInputChange = (event) => {
    acceptFile(event.target.files?.[0]);
    event.target.value = "";
  };

  const handleChange = (event) => {
    const { name, id, value } = event.target;
    const fieldName = name || id;
    if (fieldName === "title") {
      setIsTitleEdited(true);
    }
    setFormState((current) => ({ ...current, [fieldName]: value }));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    acceptFile(event.dataTransfer.files?.[0]);
  };

  const handleUpload = async () => {
    setUploadProgress(0);
    try {
      const uploaded = await uploadMutation.mutateAsync();
      await queryClient.invalidateQueries(["cmmsAssetDocuments", companyKey, assetNodeId]);
      pushNotification(intl.formatMessage(messages.uploadSuccess));
      onClose();
      // Not awaited: whatever the caller does next reports its own errors, and they are not
      // upload failures.
      onUploaded?.(uploaded);
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.uploadFailed)));
    } finally {
      setUploadProgress(null);
    }
  };

  const isUploading = uploadMutation.isLoading;
  const canSubmit = Boolean(file) && formState.title.trim().length > 0 && !isUploading;
  const isPhoto = Number(formState.documentType) === AssetDocumentType.Photo;

  return (
    <Popup openPopup={open} setOpenPopup={onClose} maxWidth="sm" title={intl.formatMessage(messages.uploadDialogTitle)}>
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <Box sx={getDialogSectionBoxSx(cmmsColors)}>
          <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontSize: 14 }}>
            <FormattedMessage {...messages.uploadDialogIntro} values={{ max: formatFileSize(uploadLimits.maxBytes) }} />
          </Typography>
        </Box>

        <Box
          onDragOver={(event) => { event.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          sx={{
            border: `2px dashed ${dragOver ? cmmsColors.text : cmmsColors.border}`,
            backgroundColor: dragOver ? cmmsColors.surfaceHover : "transparent",
            borderRadius: 1,
            p: 3,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: cmmsColors.mutedText, mb: 1.5 }}>
            {file
              ? `${file.name} (${formatFileSize(file.size)})`
              : intl.formatMessage(messages.uploadDropHint)}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ flexWrap: "wrap", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              sx={getOutlinedButtonSx(cmmsColors)}
            >
              <FormattedMessage {...(file ? messages.uploadChooseAnother : messages.uploadChooseFile)} />
            </Button>
            {isPhoto &&
              <Button
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={() => cameraInputRef.current?.click()}
                disabled={isUploading}
                sx={getOutlinedButtonSx(cmmsColors)}
              >
                <FormattedMessage {...messages.uploadTakePhoto} />
              </Button>
            }
          </Stack>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedContentTypes?.join(",")}
            hidden
            onChange={handleFileInputChange}
          />
          {/* Separate input so the camera is only requested for the Photo type —
              capture on a general picker would hide the file browser on mobile. */}
          <input
            ref={cameraInputRef}
            type="file"
            accept={acceptedContentTypes?.join(",") ?? "image/*"}
            capture="environment"
            hidden
            onChange={handleFileInputChange}
          />
        </Box>

        <Box sx={{ display: "grid", gap: 2 }}>
          <AxionControls.AxionSelect
            name="documentType"
            label={intl.formatMessage(messages.fieldType)}
            value={formState.documentType}
            onChange={handleChange}
            options={typeOptions}
            showNoneOption={false}
          />
          <AxionControls.AxionTextBox
            name="title"
            label={intl.formatMessage(messages.fieldTitle)}
            value={formState.title}
            onChange={handleChange}
          />
          <AxionControls.AxionMultiLineText
            name="description"
            label={intl.formatMessage(messages.fieldDescription)}
            value={formState.description}
            rows={3}
            onChange={handleChange}
          />
        </Box>

        {uploadProgress !== null &&
          <Box>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="caption" sx={{ color: cmmsColors.mutedText }}>
              <FormattedMessage {...messages.uploadProgress} values={{ percent: uploadProgress }} />
            </Typography>
          </Box>
        }

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={isUploading} sx={getOutlinedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.cancel} />
          </Button>
          <Button variant="contained" onClick={handleUpload} disabled={!canSubmit} sx={getContainedButtonSx(cmmsColors)}>
            <FormattedMessage {...messages.uploadSubmit} />
          </Button>
        </Stack>
      </Box>
    </Popup>
  );
}
